import {
  VStack,
  HStack,
  Box,
  Image,
  Text,
  Icon,
  Link as ChakraLink,
  useColorModeValue,
} from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import SidebarShortcuts from "./SidebarShortcuts";
import {
  BookmarkSimpleIcon,
  GraduationCapIcon,
  HandsPrayingIcon,
  ImageIcon,
  MagnifyingGlassIcon,
  ArchiveBoxIcon,
  MegaphoneIcon,
  MoonStarsIcon,
  MusicNotesIcon,
  PhoneIcon,
  UsersIcon,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import { useNav } from "../../context/NavContext";
import { useAuth } from "../../context/AuthContext";

const SidebarContent = ({ onClose }: { onClose?: () => void }) => {
  const hoverBg = useColorModeValue("primary.600", "primary.700");
  const activeBg = useColorModeValue("primary.600", "primary.700");
  const textColor = useColorModeValue("white", "gray.100");
  const { closeNav } = useNav();
  const { t } = useTranslation();
  const { appUserProfile } = useAuth();
  const logoSrc = useColorModeValue(
    "/images/logo.webp",
    "/images/logo-dark.png",
  );
  const logoSize = useColorModeValue("100%", "77%");
  const logoBg = useColorModeValue("white", "#252E3F");

  const shouldShowDashboardShortcut =
    !!appUserProfile?.role && appUserProfile.role !== "member";

  const menuItems = [
    { label: t("menu.teachings"), icon: GraduationCapIcon, path: "/teachings" },
    { label: t("menu.search"), icon: MagnifyingGlassIcon, path: "/search" },
    {
      label: t("menu.exhortations"),
      icon: MegaphoneIcon,
      path: "/exhortations",
    },
    {
      label: t("menu.zoomPrayer"),
      icon: HandsPrayingIcon,
      path: "/zoom-prayers",
    },
    {
      label: t("menu.nightVigils"),
      icon: MoonStarsIcon,
      path: "/night-vigils",
    },
    { label: t("menu.hymns"), icon: MusicNotesIcon, path: "/hymns" },
    {
      label: t("menu.favorites"),
      icon: BookmarkSimpleIcon,
      path: "/favorites",
    },
    { label: t("menu.archive"), icon: ArchiveBoxIcon, path: "/archives" },
    { label: t("menu.gallery"), icon: ImageIcon, path: "/gallery" },
    { label: t("menu.contact"), icon: PhoneIcon, path: "/contact" },
    { label: t("menu.aboutUs"), icon: UsersIcon, path: "/about-us" },
  ];

  return (
    <VStack
      h="100vh"
      align="stretch"
      w="full"
      justify="space-between"
      py="16px"
    >
      <VStack h="100%" align="stretch" w="full" spacing={1} py="16px">
        {/* Logo Section */}
        <HStack px="16px" alignItems="center" mb="32px" ml="8px" spacing="16px">
          <Box
            w="56px"
            h="56px"
            flexShrink={0}
            bg={logoBg}
            borderRadius="md"
            boxShadow="md"
            overflow="hidden"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={logoSrc}
              alt="FJC Logo"
              w={logoSize}
              h={logoSize}
              align="center"
              objectPosition="center"
              objectFit="contain"
            />
          </Box>

          <Text
            fontWeight="600"
            fontSize="xl"
            lineHeight="short"
            color="white"
            fontFamily="DancingScript"
            noOfLines={2}
          >
            Famille en Jésus-Christ
          </Text>
        </HStack>

        {/* Menu */}
        <VStack
          id="onboarding-sidebar-menu"
          w="full"
          overflowX="hidden"
          overflowY="auto"
          sx={{
            "&::-webkit-scrollbar": {
              width: "8px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "gray",
              borderRadius: "16px",
            },
            "&::-webkit-scrollbar-track": {
              background: "gray.300",
              borderRadius: "16px",
            },
          }}
          h={
            shouldShowDashboardShortcut
              ? { base: "66vh", md: "60vh", xl: "67vh", "2xl": "70vh" }
              : { md: "70vh", xl: "77vh", "2xl": "85vh" }
          }
          spacing="8px"
          align="stretch"
          px="16px"
        >
          {menuItems.map((item) => (
            <ChakraLink
              as={NavLink}
              to={item.path}
              key={item.path}
              onClick={() => {
                onClose?.();
                closeNav();
              }}
              _hover={{ bg: hoverBg }}
              _activeLink={{
                bg: activeBg,
                transform: "scale(1.02)",
              }}
              borderRadius="md"
              px={3}
              py={2}
              w="100%"
              transition="all 0.15s ease"
            >
              <HStack spacing={3}>
                <Icon
                  as={item.icon as React.ElementType}
                  boxSize="24px"
                  color="white"
                />
                <Text fontSize="md" fontWeight="medium" color={textColor}>
                  {item.label}
                </Text>
              </HStack>
            </ChakraLink>
          ))}
        </VStack>
      </VStack>
      {shouldShowDashboardShortcut && <SidebarShortcuts />}
    </VStack>
  );
};

export default SidebarContent;
